import { AbstractO2dElement, html, customElement, property, type TemplateResult } from '@o2d/shared';
import { takeUntil } from 'rxjs';
import type { O2dWhiteboardElement } from '../elements/o2d-whiteboard.element.js';
import type { BoardObject, ListType } from '../core/interfaces/board-object.interface.js';
import { OPTIONS_CIRCLES, resolveProperty, TOGGLE_INTENTS, LINE_CAP_INTENTS, SELECTION_FORMAT_INTENTS, type ActionsMap, type OptionsCircleDef } from './optionsbar-config.js';
import type { ActionIntent, SvgActionEntry } from './optionsbar-actions.js';
import type { Tool } from '../core/tool/tool.types.js';
import type { StagedToolProperties } from '../core/bus/whiteboard-bus.js';
import type { MutableObjectKey } from '../core/command/update-property.command.js';
import type { AlignMode } from '../core/command/align-objects.helper.js';
import { fontIndexToWorldUnits, worldUnitsToFontIndex, fontIndexGroup, checkboxSizesForScale } from '../core/viewport/zoom-cluster.service.js';
import type { SelectionDimension, SelectionMarkSnapshot, SelectionMarkState } from '../features/textbox/selection-mark-state.type.js';
import {
  readDocumentFormatState,
  type DocumentDimension,
  type DocumentFormatSnapshot,
} from '../features/textbox/document-format-state.js';
import type { TextBox } from '../features/textbox/textbox.interface.js';
import { objectId } from '../core/bus/branded-types.js';
import { isLine, linePathStrategyOf } from '../features/line/line.types.js';
import { FALLBACK_STRATEGY_ID } from '../features/line/strategies/line-path-strategy.interface.js';
import type { LineObject } from '../features/line/line.interface.js';

/**
 * `<o2d-svg-optionsbar>` — der „Optionskreis". Eine Lit-Komponente, die
 * je nach Auswahl ein passendes SVG aus `OPTIONS_CIRCLES` einblendet:
 * Textfeld zeigt den Text-Optionskreis, Rechteck den Rechteck-Kreis,
 * usw. Wenn nichts oder ein nicht abgedeckter Typ ausgewählt ist, wird
 * der `default`-Kreis gezeigt.
 *
 * Innerhalb jedes SVGs wird jedes `action-*`-Element per `id` an die
 * `actions`-Map des jeweiligen Kreises gebunden. Klick → setzt die
 * passende BoardObject-Property; aktive Auswahl bekommt die CSS-Klasse
 * `is-active` zur Hervorhebung.
 */
@customElement('o2d-svg-optionsbar')
export class O2dSvgOptionsbarElement extends AbstractO2dElement {
  /**
   * The whiteboard whose selection this options bar drives.
   * @deprecated PRD-H: The wrapper `<o2d-whiteboard>` now renders the options bar
   * internally and injects this reference automatically. Hosting the options bar
   * standalone and assigning `board` manually is retained for tests/back-compat
   * only and will be removed in a future release.
   */
  @property({ attribute: false }) board?: O2dWhiteboardElement;

  /** Die Aktionen-Map des aktuell sichtbaren Kreises (für click + highlight). */
  private currentActions: ActionsMap = {};
  /**
   * Welcher Typ-Schlüssel aktuell rendert. Startwert `''` = "noch nichts
   * gerendert" (derselbe Sentinel, den der readonly-Zweig setzt). WICHTIG: NICHT
   * mit `'default'` vorbelegen — sonst würde der erste `swapCircleForContext`
   * beim frischen Laden (Schlüssel `default`, nichts ausgewählt) sofort am
   * `key === currentKey`-Guard abbrechen und der Standardkreis nie erscheinen.
   */
  private currentKey: string = '';

  /** Cleanup callbacks for board listeners. */
  private boardTeardown: Array<() => void> = [];

  /**
   * Live per-run formatting snapshot of the active textbox edit selection
   * (O2D.W-PRD-027 §TR3). Set on `edit/selection-changed`, cleared on
   * `edit/selection-cleared` / `edit/commit` / `edit/cancel`. When present it
   * drives the 3-state highlight for the eight per-run marks; when `undefined`
   * those marks fall back to the object-prop matching path.
   */
  private liveMarks?: SelectionMarkSnapshot;

  /**
   * O2D.W-PRD-035 §R2 — the current selection's list block type while a textbox
   * is edited (`bullet`/`ordered`/`checklist`, or `null` for a plain paragraph).
   * `undefined` means NOT editing → list buttons fall back to the advisory
   * `obj.listType`. Captured on `edit/selection-changed`, cleared on
   * `edit/selection-cleared`/`edit/commit`/`edit/cancel`. The editing predicate
   * keys off `!== undefined` (NOT `liveMarks`) so a plain-paragraph caret
   * (`null` block, `liveMarks` possibly undefined) still resolves correctly and
   * cannot leak stale list-active state past edit-exit.
   */
  private liveListBlock?: 'bullet' | 'ordered' | 'checklist' | null;

  /**
   * Anke, 2026-07-16 — the alignment at the current edit selection, with the
   * textbox-wide default already substituted for unaligned blocks (the
   * controller resolves that, since it holds the object). `undefined` means NOT
   * editing → alignment falls back to the box-level read / `obj.hAlign`.
   *
   * Alignment is per PARAGRAPH, which is how the click already applied it — this
   * field is the missing READ half. Without it the align buttons showed
   * `obj.hAlign` (the box default) throughout the whole session, so aligning a
   * single paragraph moved the text but never the highlight.
   */
  private liveAlign?: SelectionMarkState;

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('board')) {
      this.unsubscribeBoard();
      if (this.board) this.subscribeBoard(this.board);
      this.swapCircleForContext();
    }
  }

  override disconnectedCallback(): void {
    this.unsubscribeBoard();
    window.removeEventListener('resize', this.onWindowResize);
    super.disconnectedCallback();
  }

  /** Tear down all current board-bus / board-state subscriptions. Idempotent. */
  private unsubscribeBoard(): void {
    for (const off of this.boardTeardown.splice(0)) off();
  }

  /** (Re-)subscribe to the given board's state events and bus streams. */
  private subscribeBoard(board: O2dWhiteboardElement): void {
    // Highlights also need to refresh whenever any object changes.
    const bs = board.boardState;
    this.boardTeardown.push(
      bs.on('object-updated', () => this.refreshActiveStates()),
      bs.on('object-removed', () => this.refreshActiveStates()),
    );
    // Subscribe to selection$ + tool$ via the bus (PRD-G T16, KISS C3 — no controller).
    const bus = board.bus;
    if (bus) {
      const selSub = bus.selection$
        .pipe(takeUntil(bus.destroy$))
        .subscribe(() => this.swapCircleForContext());
      const toolSub = bus.tool$
        .pipe(takeUntil(bus.destroy$))
        .subscribe(() => this.swapCircleForContext());
      // O2D.W-PRD-027 §TR3: consume the live per-run selection snapshot so the
      // 8 mark buttons reflect the highlighted run while a textbox is edited.
      const uiSub = bus.uiActions$
        .pipe(takeUntil(bus.destroy$))
        .subscribe((action) => this.onUiAction(action));
      this.boardTeardown.push(
        () => selSub.unsubscribe(),
        () => toolSub.unsubscribe(),
        () => uiSub.unsubscribe(),
      );
    }
  }

  /**
   * React to edit-lifecycle bus actions that carry / clear the live selection
   * snapshot (O2D.W-PRD-027 §TR3). All other actions are ignored here.
   */
  private onUiAction(action: {
    readonly type: string;
    readonly marks?: SelectionMarkSnapshot;
    readonly listBlock?: 'bullet' | 'ordered' | 'checklist' | null;
    readonly alignBlock?: SelectionMarkState;
  }): void {
    switch (action.type) {
      case 'edit/selection-changed':
        this.liveMarks = action.marks;
        // O2D.W-PRD-035 §R2: capture the selection block type alongside marks.
        this.liveListBlock = action.listBlock;
        // Anke, 2026-07-16: ditto the caret's alignment — re-emitted on every
        // caret move, so the highlight tracks the paragraph the caret is in.
        this.liveAlign = action.alignBlock;
        this.refreshActiveStates();
        break;
      case 'edit/selection-cleared':
      case 'edit/commit':
      case 'edit/cancel':
        // O2D.W-PRD-035 §R2/T3.5: clear ALL THREE — the guard must NOT be gated
        // on `liveMarks` alone, else a session ending with the caret in a plain
        // paragraph (liveMarks undefined, liveListBlock set) would leak stale
        // list-active state past edit-exit.
        if (
          this.liveMarks !== undefined
          || this.liveListBlock !== undefined
          || this.liveAlign !== undefined
        ) {
          this.liveMarks = undefined;
          this.liveListBlock = undefined;
          this.liveAlign = undefined;
          this.refreshActiveStates();
        }
        break;
      default:
        break;
    }
  }

  /**
   * Re-evaluates the circle after `board.readonly` flipped. Called by
   * `<o2d-whiteboard>`'s `#applyReadonly()`.
   *
   * Bugfix, Anke 2026-10-17 ("wenn ich ein Whiteboard bearbeite und dann auf
   * speichern klicke, dann bleibt ein teil des optionskreises stehen"): the
   * circle only re-renders on `selection$`/`tool$`. Locking with nothing
   * selected and the `select` tool active changes neither, so the default
   * circle stayed on the locked board. Unlocking had the mirror gap: the
   * default circle only appeared with the next selection or tool change.
   */
  public refreshForReadonly(): void {
    this.swapCircleForContext();
  }

  /**
   * Forces re-subscription to the current `board.bus` (W1 PRD-H1).
   *
   * Called by `<o2d-whiteboard>`'s `#injectBusIntoChildren()` after a
   * disconnect+reconnect cycle. Lit's `updated('board')` hook does NOT fire
   * when the wrapper reassigns the same board reference, so we cannot rely
   * on the property-change path to re-wire to the NEW bus instance.
   *
   * Idempotent: safe to call multiple times — `unsubscribeBoard()`
   * tears the subscription list down before `subscribeBoard()` rebuilds it.
   */
  public refreshBusBindings(): void {
    this.unsubscribeBoard();
    if (this.board) this.subscribeBoard(this.board);
  }

  protected override render(): TemplateResult {
    return html`<div class="svg-optionsbar-host"></div>`;
  }

  protected override firstUpdated(): void {
    this.swapCircleForContext();
    this.installDragToMove();
    // Keep a dragged circle inside the viewport when the window is resized so
    // it can never end up scrolled out of view (see `onWindowResize`).
    window.addEventListener('resize', this.onWindowResize);
    // One frame late: the toolbar (a sibling custom element rendered by the
    // same wrapper) may not have committed ITS first render yet at this exact
    // point, and applyDefaultAnchor() needs its real rendered height.
    requestAnimationFrame(() => this.applyDefaultAnchor());
  }

  /**
   * Edge anchor captured at the end of a drag: which horizontal / vertical edge
   * the circle is CLOSEST to, and its gap (px) from that edge. Null until the
   * user has dragged the bar (before that the default top-right anchor from
   * `applyDefaultAnchor()` applies and already survives resizes on its own).
   */
  private dragAnchor: { hx: 'left' | 'right'; gx: number; vy: 'top' | 'bottom'; gy: number } | null = null;

  /** Minimum gap (px) kept between the circle and the container edge. */
  private static readonly EDGE_MARGIN = 8;

  /** Gap kept below the toolbar for the default (undragged) placement — Anke, 2026-08-17. */
  private static readonly DEFAULT_TOP_GAP_REM = 1;

  /**
   * Default (undragged) placement: top-right corner of the board, one rem
   * below the toolbar (`right: 1rem` itself is static CSS, see
   * whiteboard.css). The toolbar's rendered height is MEASURED rather than
   * hardcoded because `.toolbar-inner` wraps to multiple rows on narrow
   * layouts / with many injected buttons (`flex-wrap: wrap`) — a literal px
   * `top` would drift out from under the toolbar whenever that happens. A
   * no-op once the circle has been dragged (`dragAnchor` set) — from then on
   * `applyDragAnchor()` owns the position instead.
   */
  private applyDefaultAnchor(): void {
    if (this.dragAnchor) return;
    const parent = this.offsetParent as HTMLElement | null;
    const toolbar = parent?.querySelector('o2d-whiteboard-toolbar');
    const toolbarHeight = toolbar?.getBoundingClientRect().height ?? 0;
    this.style.top = `calc(${toolbarHeight}px + ${O2dSvgOptionsbarElement.DEFAULT_TOP_GAP_REM}rem)`;
  }

  /**
   * Record which edges the circle currently sits nearest to and its gap from
   * each, then re-apply. Called at drag-end so the position is expressed
   * relative to the NEAREST edge — that is what lets it stick to that edge when
   * the window later grows OR shrinks (`left`/`top`-only anchoring drifts inward
   * on grow). `offsetParent` is the positioned board wrapper.
   */
  private captureDragAnchor(): void {
    const parent = this.offsetParent as HTMLElement | null;
    if (!parent) return;
    const gapLeft = this.offsetLeft;
    const gapTop = this.offsetTop;
    const gapRight = parent.clientWidth - this.offsetWidth - gapLeft;
    const gapBottom = parent.clientHeight - this.offsetHeight - gapTop;
    this.dragAnchor = {
      hx: gapRight < gapLeft ? 'right' : 'left',
      gx: Math.max(0, gapRight < gapLeft ? gapRight : gapLeft),
      vy: gapBottom < gapTop ? 'bottom' : 'top',
      gy: Math.max(0, gapBottom < gapTop ? gapBottom : gapTop),
    };
    this.applyDragAnchor();
  }

  /**
   * Position the circle from its captured edge anchor, clamped so the gap never
   * exceeds what the (possibly shrunk) container can hold — keeps it reachable.
   * The stored gap is preserved, so growing the window restores the original
   * distance from the edge (the circle "sticks" to that edge).
   */
  private applyDragAnchor(): void {
    const a = this.dragAnchor;
    if (!a) return;
    const parent = this.offsetParent as HTMLElement | null;
    if (!parent) return;
    const m = O2dSvgOptionsbarElement.EDGE_MARGIN;
    const maxX = Math.max(m, parent.clientWidth - this.offsetWidth - m);
    const maxY = Math.max(m, parent.clientHeight - this.offsetHeight - m);
    const gx = Math.min(Math.max(m, a.gx), maxX);
    const gy = Math.min(Math.max(m, a.gy), maxY);
    if (a.hx === 'right') { this.style.right = `${gx}px`; this.style.left = 'auto'; }
    else { this.style.left = `${gx}px`; this.style.right = 'auto'; }
    if (a.vy === 'bottom') { this.style.bottom = `${gy}px`; this.style.top = 'auto'; }
    else { this.style.top = `${gy}px`; this.style.bottom = 'auto'; }
  }

  /**
   * Re-apply the edge anchor on every window resize (grow AND shrink) — or,
   * before any drag has happened, re-measure the toolbar in case it just
   * wrapped to a different number of rows.
   */
  private onWindowResize = (): void => {
    if (this.dragAnchor) this.applyDragAnchor();
    else this.applyDefaultAnchor();
  };

  // ---- Circle swap on selection / tool change ---------------------------

  /**
   * Pick the OptionsCircleDef for the current context. Priority:
   *   1. If a single object is selected, use its type.
   *   2. Otherwise, if a placement tool (text / sticky / rectangle / …)
   *      is active, preview that tool's circle so the user already sees
   *      the relevant options before placing the object.
   *   3. Otherwise fall back to `default`.
   * Re-binds handlers and refreshes highlights only when the circle
   * actually changes — same-circle updates just refresh highlights.
   */
  private swapCircleForContext(): void {
    // PRD-X §TR8 / DD5: in readonly mode the optionsbar must render
    // nothing — no circle, no actions. We clear the host and reset
    // bookkeeping so a later readonly=false flip rebinds cleanly.
    if (this.board?.readonly) {
      const host = this.querySelector<HTMLDivElement>('.svg-optionsbar-host');
      if (host && host.innerHTML !== '') host.innerHTML = '';
      this.currentActions = {};
      this.currentKey = '';
      return;
    }
    const key = this.pickCircleKey();
    // Anke-Wunsch: der Standardkreis (part1 = Standard-Knöpfe) wird IMMER
    // gezeigt — auch wenn nichts ausgewählt ist und kein Werkzeug aktiv ist.
    // Der 'default'-Schlüssel läuft daher durch den normalen Render-Pfad; nur
    // der readonly-Zweig oben leert den Host noch. (Früher war 'default' eine
    // handlerlose graue Platzhalter-Fläche, PRD-039 §TR1 — jetzt sichtbar.)
    if (key === this.currentKey) {
      this.refreshActiveStates();
      return;
    }
    const def: OptionsCircleDef | undefined = OPTIONS_CIRCLES[key as keyof typeof OPTIONS_CIRCLES];
    if (!def) return;
    const host = this.querySelector<HTMLDivElement>('.svg-optionsbar-host');
    if (!host) return;
    host.innerHTML = def.svg;
    this.sizeCircle(host);
    this.currentActions = def.actions;
    this.currentKey = key;
    this.bindActionHandlers();
    this.refreshActiveStates();
  }

  /**
   * Skaliert den Kreis mit einem KONSTANTEN px-pro-Einheit-Faktor, statt ihn
   * auf eine feste Breite zu strecken. Weil alle Teile dasselbe Koordinaten-
   * system teilen (die Passmarke garantiert es), sind so die Knöpfe über alle
   * Werkzeuge gleich groß — nur der Gesamtkreis wird mit mehr Knöpfen größer.
   *
   * Der Faktor kommt aus der CSS-Variable `--o2d-optionscircle-scale` (px pro
   * Einheit), damit die Konsumentenseite die Kreisgröße weiterhin über eine
   * einzige Stellschraube regeln kann. Fallback: 1.4.
   */
  private sizeCircle(host: HTMLElement): void {
    const svg = host.querySelector<SVGSVGElement>('svg');
    if (!svg) return;
    const viewBox = svg.getAttribute('viewBox');
    const dims = viewBox ? viewBox.split(/\s+/).map(Number) : null;
    if (!dims || dims.length < 4 || !Number.isFinite(dims[2]!) || !Number.isFinite(dims[3]!)) return;
    const raw = getComputedStyle(this).getPropertyValue('--o2d-optionscircle-scale').trim();
    const scale = parseFloat(raw) > 0 ? parseFloat(raw) : 1.4;
    svg.style.width = `${dims[2]! * scale}px`;
    svg.style.height = `${dims[3]! * scale}px`;
  }

  /**
   * The key under which `OPTIONS_CIRCLES` should be looked up given
   * the current selection and active tool. Returns `'default'` when
   * neither path matches a registered circle.
   */
  private pickCircleKey(): string {
    // Anke, 2026-07-23 — bei einer gleichartigen Mehrfachauswahl den typ-eigenen
    // Kreis zeigen (statt des Standard-Kreises), damit eine Einstellung auf ALLE
    // angewandt werden kann. `representativeObject` deckt beide Fälle ab: genau
    // ein Objekt ODER mehrere gleichen Typs.
    const obj = this.representativeObject();
    if (obj && OPTIONS_CIRCLES[obj.type]) return obj.type;
    const tool = this.armedPlacementTool();
    return tool ?? 'default';
  }

  /** Tool ids that correspond directly to a BoardObject type — shared between
   *  `pickCircleKey()` (which circle to preview) and the pre-placement
   *  staging path in `applyAction()`/`refreshActiveStates()` below (Stefan,
   *  2026-09-03). */
  private static readonly TOOL_AS_TYPE = new Set<Tool>([
    'text', 'sticky', 'rectangle', 'ellipse', 'line', 'person', 'checkbox', 'smiley',
  ]);

  /**
   * The currently active tool, if it is a placement tool with its OWN
   * registered options circle — the exact condition under which the circle
   * is shown as a pre-placement PREVIEW (see `pickCircleKey()`). `null`
   * otherwise (no tool armed, `select` active, or a tool without a circle).
   */
  private armedPlacementTool(): Tool | null {
    const tool = this.board?.tool;
    if (tool && tool !== 'select' && O2dSvgOptionsbarElement.TOOL_AS_TYPE.has(tool)
      && OPTIONS_CIRCLES[tool as keyof typeof OPTIONS_CIRCLES]) {
      return tool;
    }
    return null;
  }

  // ---- Drag-to-move ------------------------------------------------------

  /**
   * Make the host element draggable: pressing on any non-action area of
   * the SVG starts a drag that updates the host's `left` / `top` (the
   * host is expected to be absolutely positioned by the consumer page).
   * Pressing on an `action-*` element starts the action click instead —
   * we recognise it via `closest('[id^="action-"]')` and bail.
   */
  private installDragToMove(): void {
    // Anke, 2026-07-16: ausserhalb der Knoepfe der normale Pfeil, keine Hand.
    // Der Kreis BLEIBT verschiebbar — nur kuendigt der Ruhezustand es nicht
    // mehr an. Waehrend des Ziehens zeigen wir weiter die geschlossene Hand
    // (siehe onDragMouseDown), das ist Rueckmeldung zur laufenden Aktion und
    // nicht das, was hier stoerte.
    this.style.cursor = 'default';
    this.addEventListener('mousedown', this.onDragMouseDown);
  }

  private onDragMouseDown = (e: MouseEvent): void => {
    if (e.button !== 0) return;
    const target = e.target as Element | null;
    if (target?.closest('[id^="action-"]')) return; // action click takes precedence
    e.preventDefault();
    const startLeft = this.offsetLeft;
    const startTop = this.offsetTop;
    const startX = e.clientX;
    const startY = e.clientY;
    this.style.cursor = 'grabbing';

    const onMove = (ev: MouseEvent): void => {
      this.style.left = `${startLeft + (ev.clientX - startX)}px`;
      this.style.top  = `${startTop  + (ev.clientY - startY)}px`;
      this.style.right = 'auto';
      this.style.bottom = 'auto';
    };
    const onUp = (): void => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      this.style.cursor = 'default';
      // Re-express the dropped position relative to the nearest edges so it
      // survives later window resizes (grow AND shrink).
      this.captureDragAnchor();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ---- Action wiring -----------------------------------------------------

  private bindActionHandlers(): void {
    const root = this.querySelector<HTMLDivElement>('.svg-optionsbar-host');
    if (!root) return;
    for (const [id, action] of Object.entries(this.currentActions)) {
      const el = root.querySelector<SVGElement>(`#${cssEscape(id)}`);
      if (!el) continue;
      // Same trick as the HTML actions panel: preventDefault on mousedown
      // so an active rich-text editor doesn't lose its caret/selection
      // when the user clicks here.
      el.style.cursor = 'pointer';
      // Anke, 2026-07-16: Nur verkabelte Knoepfe reagieren beim Darueberfahren.
      // Diese Schleife laeuft ausschliesslich ueber `currentActions`, ist also
      // genau die Stelle, die "wirkt" von "nur gezeichnet" trennt — part1 malt
      // seine Knoepfe in JEDEN Kreis, auch wo sie nichts tun. Die Klasse statt
      // eines CSS-Selektors auf `[id^="action-"]`, weil das Markup allein nicht
      // verraet, ob ein Knopf einen Handler bekommen hat.
      el.classList.add('is-wired');
      el.onmousedown = (e: MouseEvent) => e.preventDefault();
      el.onclick = () => this.applyAction(id);
      // Deutschen Tooltip setzen (erscheint beim Hover als Browser-Tooltip).
      const tooltip = deriveTooltip(action.intent, action.value);
      if (tooltip) {
        // SVG-native: <title>-Element als erstes Kind einfügen.
        // Der Browser zeigt es als Tooltip an — robuster als title-Attribut
        // auf SVG-Elementen in manchen Browsern.
        let titleEl = el.querySelector<SVGTitleElement>('title');
        if (!titleEl) {
          titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
          el.insertBefore(titleEl, el.firstChild);
        }
        titleEl.textContent = tooltip;
      }
    }
  }

  private applyAction(id: string): void {
    const action = this.currentActions[id];
    if (!action || !this.board) return;

    // ---- Sonderfall: Board-Befehle ------------------------------------
    // Zwischenablage, Verlauf und Löschen brauchen kein ausgewähltes Objekt.
    if (action.intent === 'board-command') {
      switch (action.value) {
        case 'copy':   this.board.copySelected();   break;
        case 'duplicate': this.board.duplicateSelected(); break;
        case 'group':   this.board.groupSelected();   break;
        case 'degroup': this.board.ungroupSelected(); break;
        case 'cut':    this.board.cutSelected();    break;
        case 'paste':  this.board.pasteSelected();  break;
        case 'undo':   this.board.undo();           break;
        case 'redo':   this.board.redo();           break;
        case 'delete': this.board.deleteSelected(); break;
      }
      return;
    }

    // Anke, 2026-07-16 — Ausrichten wirkt auf die GANZE Auswahl, nicht auf ein
    // einzelnes Objekt. Muss darum vor dem `singleSelectedObject()`-Pfad unten
    // abzweigen, der bei Mehrfachauswahl `undefined` liefert und alles verwirft.
    if (action.intent === 'align') {
      this.board.alignSelected(action.value as AlignMode);
      return;
    }

    // Anke, 2026-07-23 — Stellvertreter statt „genau ein Objekt": bei einer
    // gleichartigen Mehrfachauswahl steht hier das erste Objekt (für Typ- und
    // Property-Auflösung sowie das Ablesen des aktuellen Werts), angewandt wird
    // aber über `board.updateSelectedProperty` auf ALLE Ausgewählten. Bei
    // gemischten Typen liefert er `undefined` → der (Standard-)Kreis hat ohnehin
    // keine typ-eigenen Einstellungen.
    const obj = this.representativeObject();
    if (!obj) {
      this.stagePrePlacementProperty(action);
      return;
    }

    // ---- Sonderfall: freie Farbwahl (Anke, 2026-07-23) -----------------
    // Das `_custom`-Feld öffnet den nativen Farbwähler des Browsers. Bewusst
    // nativ statt eines eigenen Farbrad-Widgets: ein solches würde das
    // Größenlimit sprengen (siehe [[svg-groessenlimit]]), während `input
    // type=color` praktisch nichts kostet. Die gewählte Farbe wird über GENAU
    // denselben Weg angewandt wie ein Farbfeld — value 'fill' → 'fill', 'border'
    // → 'border-color' — damit die typabhängige Property-Auflösung (Rechteck:
    // fill/strokeColor, Text: background/color …) unverändert greift.
    if (action.intent === 'custom-color') {
      const underlying: ActionIntent = action.value === 'border' ? 'border-color' : 'fill';
      const property = resolveProperty(underlying, obj.type);
      if (!property) return; // Farbe gilt nicht für diesen Objekttyp
      // Anker = der OPTIONSKREIS-Container. Idee: der native Wähler erscheint
      // dort, wo das (unsichtbare) Eingabefeld liegt.
      //
      // BEKANNTE GRENZE (Anke, 2026-07-23, im Browser bestätigt): Chrome richtet
      // den Farbdialog NICHT am Eingabefeld aus — er erscheint unabhängig von
      // dessen Position oben links. Die Verankerung bleibt trotzdem hier (kostet
      // nichts, korrekt für Browser, die die Position ehren, und der Test hält
      // sie fest), aber sie LÖST das Positions-Thema in Chrome NICHT. Ein eigenes
      // Farbfenster wäre nötig — verschoben, hängt am Größenbudget.
      const host = this.querySelector<HTMLElement>('.svg-optionsbar-host');
      const anchor = host?.getBoundingClientRect();
      this.#openColorPicker(
        (obj as unknown as Record<string, unknown>)[property],
        (picked) => { this.board?.updateSelectedProperty(property as MutableObjectKey, picked); },
        anchor,
      );
      return;
    }

    // ---- Sonderfall: Z-Reihenfolge ------------------------------------
    // Keine Property-Änderung — beide Knöpfe springen ans Ende des Stapels,
    // nicht eine Ebene weit (siehe Z_ORDER_ACTIONS).
    if (action.intent === 'z-order') {
      if (action.value === 'front')     this.board.bringToFront();
      else if (action.value === 'back') this.board.sendToBack();
      return;
    }

    // ---- Sonderfall: Linien-Enden tauschen -----------------------------
    // Kein Property-Update: der Tausch schreibt beide Kappen, beide Endpunkte
    // und beide Andockpunkte. Er geht als UiAction an den
    // LineLifecycleController — für ALLE ausgewählten Linien, als EIN
    // Rückgängig-Schritt (Anke, 2026-10-03: vorher nur die erste Linie).
    if (action.intent === 'line-swap-caps') {
      const ids = this.selectedLines().map((l) => objectId(l.id));
      if (ids.length > 0) {
        this.board.bus?.uiActions$.next({ type: 'line/swap-caps', ids });
      }
      return;
    }

    // ---- Sonderfall: Linienform setzen ---------------------------------
    // part7 hat drei getrennte Knöpfe (gerade / rechtwinklig / Bezier), jeder
    // setzt seine Ziel-Strategie direkt. Kein Property-Update: der
    // LineLifecycleController baut daraus SetLinePathStrategyCommands — für
    // ALLE ausgewählten Linien, als EIN Rückgängig-Schritt (Anke, 2026-10-03).
    // Linien, die die Form schon haben, bleiben draußen; haben alle sie schon,
    // ist der Klick ein No-Op (kein leerer Rückgängig-Schritt).
    if (action.intent === 'line-path-strategy') {
      const next = String(action.value);
      const ids = this.selectedLines()
        .filter((l) => linePathStrategyOf(l) !== next)
        .map((l) => objectId(l.id));
      if (ids.length > 0) {
        this.board.bus?.uiActions$.next({ type: 'line/set-path', ids, strategy: next });
      }
      return;
    }

    // ---- Sonderfall: Linien-Enden setzen -------------------------------
    // Ein Klick auf das bereits getragene Symbol nimmt das Ende wieder ab. Der
    // Kreis zeichnet keinen „kein Ende"-Knopf — der aktive Knopf IST er. Nicht
    // über TOGGLE_INTENTS: das löscht die Eigenschaft, und startCap/endCap sind
    // Pflichtfelder des Renderers.
    if (LINE_CAP_INTENTS.has(action.intent)) {
      const capProperty = resolveProperty(action.intent, obj.type);
      if (!capProperty) return;
      const current = (obj as unknown as Record<string, unknown>)[capProperty];
      const next = current === action.value ? 'none' : action.value;
      this.board.updateSelectedProperty(capProperty as MutableObjectKey, next);
      return;
    }

    // ---- Sonderfall: Listentyp ----------------------------------------
    // O2D.W-PRD-035 §TR2: editor-aware routing. While a textbox editor is
    // OPEN, drive the LIVE TipTap editor (selection-scoped, per-paragraph) via
    // a `textbox/apply-list` UiAction. When NOT editing, fall back to the
    // whole-textbox `setListType` command (D2) — which converts the text
    // format and has toggle (same type → 'none') built in.
    if (action.intent === 'list-type') {
      if (obj.type === 'text') {
        if (this.board.isTextboxEditing) {
          this.board.bus?.uiActions$.next({
            type: 'textbox/apply-list',
            id: objectId(obj.id),
            listType: action.value as 'bullet' | 'ordered' | 'checklist',
          });
        } else {
          this.board.setListType(obj.id, action.value as ListType);
        }
      }
      return;
    }

    // ---- Sonderfall: horizontale Ausrichtung (pro Absatz) -------------
    // Wie beim Listentyp: im Bearbeitungsmodus treibt der Klick den LIVE
    // TipTap-Editor absatzweise (`setTextAlign` am Cursor) via
    // `textbox/apply-align`. Außerhalb des Bearbeitens wirkt die Ausrichtung
    // auf die ganze Box über den `hAlign`-Standard.
    if (action.intent === 'text-align') {
      if (obj.type === 'text') {
        const align = action.value as 'left' | 'center' | 'right';
        if (this.board.isTextboxEditing) {
          this.board.bus?.uiActions$.next({
            type: 'textbox/apply-align',
            id: objectId(obj.id),
            align,
          });
        } else {
          this.board.updateSelectedProperty('hAlign', align);
        }
      }
      return;
    }

    // ---- Sonderfall: symbolische Schriftgröße -------------------------
    // Der Aktionswert ist ein Feinstufen-Index 1–24. Bei der Textbox rechnet das
    // Board den em-Wert selbst aus (cluster-skaliert) und wendet ihn auf die
    // Textauswahl oder das ganze Objekt an. Objekte, die ihr Label direkt mit
    // Konva zeichnen (Person), speichern die Größe als Welteinheiten — die Bühne
    // skaliert sie beim Zoomen ohnehin mit.
    if (action.intent === 'text-size') {
      if (obj.type === 'text') {
        this.board.applyFontLevelToSelection(action.value as number);
      } else {
        const sizeProp = resolveProperty(action.intent, obj.type);
        if (sizeProp) {
          const wu = fontIndexToWorldUnits(action.value as number);
          this.board.updateSelectedProperty(sizeProp as MutableObjectKey, wu);
        }
      }
      return;
    }

    // ---- Sonderfall: Checkbox-Größe (S/M/L) ----------------------------
    // Stefan, 2026-08-29 — abgelöst von der alten (inzwischen komplett
    // entfernten) Aktionsleiste (dort `pickCheckboxSize`), gleicher
    // Mechanismus: der Klick setzt `width` UND `height` gemeinsam, also kein
    // einzelnes Property wie beim generischen Pfad unten. Die tatsächliche
    // Weltgröße kommt LIVE aus `checkboxSizesForScale(scale)` (gekoppelt an
    // den aktuellen Zoom, siehe zoom-cluster.service.ts) — `action.value`
    // trägt nur den Größen-Schlüssel S/M/L.
    //
    // Review-01, Linse B (2026-08-29): `updateSelectedProperties` (statt
    // zweier `updateSelectedProperty`-Aufrufe) bündelt width+height in EINEN
    // Undo-Schritt — ein Klick = ein Rückgängig-Schritt.
    if (action.intent === 'checkbox-size' && obj.type === 'checkbox') {
      const scale = this.board.bus?.viewport$.value.scale ?? 1;
      const key = action.value as 'S' | 'M' | 'L';
      const size = checkboxSizesForScale(scale)[key];
      this.board.updateSelectedProperties([['width', size], ['height', size]]);
      return;
    }

    const property = resolveProperty(action.intent, obj.type);
    if (!property) return; // intent gilt nicht für diesen Objekttyp

    // ---- Inline-Formate für markierten Abschnitt ----------------------
    // Nur bei der TEXTBOX: diese Intents wirken im Bearbeitungsmodus auf die
    // Textauswahl. updateSelectedProperty im Board-Element leitet sie an
    // applyTextFormatToSelection weiter; außerhalb des Bearbeitungsmodus
    // passiert nichts (kein Objekt-Property-Update).
    //
    // Andere Typen (Person) haben keinen Editor — für sie sind es gewöhnliche
    // Objekt-Eigenschaften. Sie fallen deshalb bis zur Toggle-Logik durch, in
    // der 'font-style' und 'text-decoration' als Ein/Aus-Schalter stehen.
    if (obj.type === 'text' && SELECTION_FORMAT_INTENTS.has(action.intent)) {
      this.board.updateSelectedProperty(property as MutableObjectKey, action.value);
      return;
    }

    // ---- Toggle-Logik --------------------------------------------------
    // Bei Toggle-Intents: ist der aktuelle Wert bereits gleich dem Aktionswert,
    // wird die Eigenschaft durch `undefined` gelöscht (= ausgeschaltet).
    if (TOGGLE_INTENTS.has(action.intent)) {
      const currentValue = (obj as unknown as Record<string, unknown>)[property];
      if (currentValue === action.value) {
        this.board.updateSelectedProperty(property as MutableObjectKey, undefined);
        return;
      }
    }

    this.board.updateSelectedProperty(property as MutableObjectKey, action.value);
    // Die selection/object-updated-Kette sorgt für das Auffrischen der is-active-Klassen.
  }

  /**
   * Stefan, 2026-09-03 — "Eigenschaften vor dem Zeichnen wählen": der zweite,
   * gleichwertige Weg neben "zeichnen, dann im Kreis ändern". Greift NUR, wenn
   * kein Objekt ausgewählt ist, aber ein Platzierungswerkzeug mit eigenem
   * Optionskreis aktiv ist (`armedPlacementTool()` — dieselbe Bedingung, unter
   * der der Kreis heute schon als reine VORSCHAU gezeigt wird, siehe
   * `pickCircleKey()`). Merkt den aufgelösten Objekt-Property-Wert im Bus vor
   * (`bus.setStagedToolProperty`), statt ihn — wie der generische Pfad am Ende
   * von `applyAction()` — direkt auf ein (nicht existierendes) Objekt zu
   * schreiben. Die Platzierungs-Controller lesen den vorgemerkten Wert beim
   * tatsächlichen Zeichnen und reichen ihn an die jeweilige
   * `create<Type>Defaults()`-Factory durch.
   *
   * Bewusst NUR der generische `resolveProperty`-Pfad plus ein paar benannte
   * Sonderfälle unten — dieselben Sonderfälle, die weiter oben in
   * `applyAction()` bereits VOR dem `if (!obj)`-Zweig behandelt werden
   * (`board-command`, `align`), erreichen diese Methode nie; die übrig
   * bleibenden werden hier bewusst ausgeschlossen — sie ergeben vor dem
   * Platzieren fachlich keinen Sinn (z. B. Ebenen-Reihenfolge, oder
   * `line-swap-caps` — es gibt vor dem Zeichnen noch keine zwei Enden zum
   * Tauschen).
   *
   * `text-size`/`text-align` (Bugreport Anke, 2026-09-03: "Schriftgröße,
   * horizontale Ausrichtung funktioniert noch nicht"),
   * `line-cap-start`/`-end`/`line-path-strategy` (Bugreport Anke,
   * 2026-09-03: "funktioniert vorauswahl nicht für: linienenden,
   * linienform") und `checkbox-size` (Bugreport Anke, 2026-09-03:
   * "vorauswahl von größe... wird nicht angezeigt") waren hier ANFANGS
   * ebenfalls ausgeschlossen — post-Platzierung haben sie zusätzliche
   * Editor-Routing-/Cluster-Skalierungs-/Kommando-Logik bzw. (Checkbox) eine
   * Mehrfach-Property-Semantik, die vor der Objekt-Existenz keinen Sinn
   * ergibt, aber der GRUNDWERT (Weltgrößen-Schriftgröße, `hAlign`,
   * `startCap`/`endCap`, `pathStrategy`, die abstrakte S/M/L-Größe) lässt
   * sich trotzdem sauber vormerken — siehe die Sonderfälle unten in
   * `stagePrePlacementProperty()`/`refreshActiveStates()`.
   */
  private static readonly NOT_STAGEABLE_INTENTS = new Set<ActionIntent>([
    'custom-color',
    'z-order',
    'line-swap-caps',
    'list-type',
  ]);

  private stagePrePlacementProperty(action: SvgActionEntry): void {
    const tool = this.armedPlacementTool();
    if (!tool) return;
    if (O2dSvgOptionsbarElement.NOT_STAGEABLE_INTENTS.has(action.intent)) return;
    // `line-path-strategy` has NO resolveProperty mapping at all (post-
    // creation it's a dedicated command, `line/set-path`, not a plain
    // property write) — stage it directly under `pathStrategy`, the exact
    // field `createLineDefaults`'s options already accept.
    if (action.intent === 'line-path-strategy') {
      if (tool === 'line') {
        this.board?.bus?.setStagedToolProperty(tool, 'pathStrategy', action.value);
        this.refreshActiveStates();
      }
      return;
    }
    // Bugreport (Anke, 2026-09-03): "checkbox: vorauswahl von größe...
    // wird nicht angezeigt" — `checkbox-size` also has NO resolveProperty
    // mapping (post-creation it sets width+height together, live-scaled to
    // the CURRENT viewport at click time via `checkboxSizesForScale`, see
    // `applyAction()`'s own special case). Staging the abstract S/M/L KEY
    // (not a pre-computed world size) under `sizeKey` keeps that same
    // "resolve against the CURRENT scale" behaviour for the placement
    // itself (`checkbox-placement.controller.ts`), rather than baking in
    // whatever scale happened to be active at staging time.
    if (action.intent === 'checkbox-size') {
      if (tool === 'checkbox') {
        this.board?.bus?.setStagedToolProperty(tool, 'sizeKey', action.value);
        this.refreshActiveStates();
      }
      return;
    }
    const property = resolveProperty(action.intent, tool as unknown as BoardObject['type']);
    if (!property) return;
    // `text-size`'s action value is a fine-step index (1–24, see the SVG's
    // part3 buttons) — convert to the world-unit value the factory's
    // `fontSize` field (and the post-creation object property) actually
    // store, exactly like the generic non-text branch further down in
    // `applyAction()` already does for a REAL selected object.
    const value = action.intent === 'text-size'
      ? fontIndexToWorldUnits(action.value as number)
      : action.value;
    // Review-Lauf 01 (2026-09-03): mirror `applyAction()`'s own TOGGLE_INTENTS
    // handling (line 719 above) — a second click on an already-staged toggle
    // value (e.g. box-shadow "on") un-stages it, instead of the vormerkung
    // being stuck re-setting the same value with no way back before drawing.
    if (TOGGLE_INTENTS.has(action.intent)) {
      const stagedProps = this.board?.bus?.stagedToolProperties$.value[tool];
      if (stagedProps?.[property] === value) {
        this.board?.bus?.clearStagedToolProperty(tool, property);
        this.refreshActiveStates();
        return;
      }
    }
    this.board?.bus?.setStagedToolProperty(tool, property, value);
    this.refreshActiveStates();
  }

  /**
   * Walk all `action-*` elements and toggle the `is-active` class based
   * on whether their `value` matches the current selected object's
   * matching property.
   */
  private refreshActiveStates = (): void => {
    const root = this.querySelector<HTMLDivElement>('.svg-optionsbar-host');
    if (!root) return;
    const obj = this.singleSelectedObject();
    // Stefan, 2026-09-03: only relevant when NOTHING is selected — an actual
    // selection always takes priority (matches `applyAction()`'s own
    // ordering: staging only ever kicks in via the `!obj` branch there).
    const stagedTool = obj ? null : this.armedPlacementTool();
    const stagedProps = stagedTool ? this.board?.bus?.stagedToolProperties$.value[stagedTool] : undefined;
    // Read ONCE per refresh, not per button: the walk is O(document) and the
    // loop below runs for ~100 buttons (30 colour swatches alone).
    const boxFormat =
      obj?.type === 'text' && !this.#isEditingText()
        ? readDocumentFormatState(obj as TextBox)
        : null;
    // Anke, 2026-10-03 — several selected lines: the line-form button of the
    // form ALL of them share lights; mixed forms light none. (The other
    // settings still light only for a single selection.)
    const multiLines = obj ? [] : this.selectedLines();
    for (const [id, action] of Object.entries(this.currentActions)) {
      const el = root.querySelector<SVGElement>(`#${cssEscape(id)}`);
      if (!el) continue;
      let active = false;
      let mixed = false;
      if (action.intent === 'line-path-strategy' && multiLines.length > 1) {
        active = multiLines.every((l) => linePathStrategyOf(l) === action.value);
        el.classList.toggle('is-active', active);
        el.classList.toggle('is-mixed', false);
        continue;
      }
      // Anke, 2026-07-16 — a SELECTED (not edited) textbox describes the text you
      // actually see, by reading the whole document instead of the container
      // props. Those props are not a truthful summary: `fontWeight` is never
      // written by a whole-box bold (DD3, `wholeDocFormatSpec`), so an all-bold
      // box used to light "normal"; and a box whose runs were individually
      // recoloured still lit its own default swatch, a colour possibly absent
      // from the box. This branch must precede BOTH the list short-circuit and
      // the object-prop path, and covers every dimension the box can hold.
      if (boxFormat) {
        const boxDim = intentToDocumentDimension(action.intent, action.value);
        if (boxDim) {
          ({ active, mixed } = this.resolveBoxState(boxDim, action.value, boxFormat, id));
          el.classList.toggle('is-active', active);
          el.classList.toggle('is-mixed', mixed);
          continue;
        }
      }
      // O2D.W-PRD-035 §R2: list buttons resolve selection-scoped WHILE EDITING
      // (from the captured block type) and short-circuit BEFORE the whole-textbox
      // `propertyMatches(obj,'listType')` fallback; when NOT editing they fall
      // through to `obj.listType`. The while-editing predicate keys off the
      // presence of `liveListBlock` (NOT `liveMarks`).
      if (action.intent === 'list-type') {
        active = this.resolveListActive(action.value, obj);
        el.classList.toggle('is-active', active);
        el.classList.toggle('is-mixed', false);
        continue;
      }
      // Anke, 2026-07-16 — WHILE EDITING the align buttons follow the caret's
      // paragraph, mirroring the list branch above. This must short-circuit the
      // object-prop path, which resolves `obj.hAlign` — the whole-box default
      // that a per-paragraph align never touches.
      if (action.intent === 'text-align' && this.liveAlign) {
        active = this.resolveAlignActive(action.value);
        el.classList.toggle('is-active', active);
        // Blocks disagreeing shows nothing, per Anke's rule for alignment.
        el.classList.toggle('is-mixed', false);
        continue;
      }
      // O2D.W-PRD-027 §TR3: while a textbox is edited, the eight per-run marks
      // read the live selection snapshot (3-state); object props and the
      // no-snapshot case keep the existing 2-state object-prop matching.
      //
      // An OFF mark deliberately falls THROUGH to the object-prop path (Anke,
      // 2026-07-16). OFF means the run carries no explicit mark of its own — its
      // effective formatting is then the textbox-wide property, which is exactly
      // what `buildTextboxHtmlElement` writes onto the wrapper and what the user
      // sees. Without the fallthrough a freshly-typed textbox lit NO weight and
      // NO colour button while typing, because untouched text has no marks.
      // MIXED keeps its own (dashed) resolution; ON keeps the run's value.
      const dimension = this.liveMarks ? intentToDimension(action.intent, action.value) : null;
      if (dimension && this.liveMarks && this.liveMarks[dimension].state !== 'off') {
        ({ active, mixed } = this.resolveMarkState(dimension, action.value, this.liveMarks, id));
      } else if (obj) {
        if (action.intent === 'text-size' && obj.type === 'text') {
          // fontSize ist seit Anke, 2026-08-13 NICHT mehr cluster-geteilt
          // gespeichert (siehe zoom-cluster.service.ts) — der Wert ist bereits
          // der echte Welteinheiten-Wert, keine Rückrechnung mit dem aktuellen
          // Zoom-Faktor mehr nötig (das war der Bug: der Index änderte sich je
          // nach aktuellem Zoom, obwohl sich am Objekt nichts geändert hatte).
          const fontSize = (obj as unknown as Record<string, unknown>)['fontSize'] as number ?? 16;
          active = this.#fontSizeButtonActive(id, action.value as number, worldUnitsToFontIndex(fontSize));
        } else if (action.intent === 'text-size') {
          // Konva-gezeichnete Labels (Person) speichern reine Welteinheiten —
          // kein Cluster-Faktor. Ohne gesetzte Größe leuchtet keine Stufe.
          const sizeProp = resolveProperty(action.intent, obj.type);
          const fontSize = sizeProp
            ? ((obj as unknown as Record<string, unknown>)[sizeProp] as number | undefined)
            : undefined;
          active = fontSize !== undefined
            && this.#fontSizeButtonActive(id, action.value as number, worldUnitsToFontIndex(fontSize));
        } else if (action.intent === 'line-path-strategy' && isLine(obj)) {
          // Linienführung ist ein Befehl, kein Property — der Knopf, dessen
          // Ziel-Strategie die aktuelle ist, leuchtet. Ohne gesetzte Strategie
          // gilt 'straight'. Der Umschalt-Knopf ('toggle') passt nie → inert.
          active = linePathStrategyOf(obj) === action.value;
        } else if (action.intent === 'checkbox-size' && obj.type === 'checkbox') {
          // Stefan, 2026-08-29 — wie bei text-size/line-path-strategy: kein
          // einzelnes Property (der Klick setzt width UND height zugleich),
          // resolveProperty liefert für diesen Intent bewusst null. Der
          // Knopf leuchtet, dessen S/M/L-Weltgröße bei der AKTUELLEN
          // Zoomstufe der Breite des Objekts entspricht (checkboxSizesForScale
          // ist live an den Zoom gekoppelt, siehe checkbox-options.config.ts).
          const scale = this.board?.bus?.viewport$.value.scale ?? 1;
          const sizes = checkboxSizesForScale(scale);
          const key = action.value as 'S' | 'M' | 'L';
          active = Math.abs((obj as unknown as { width: number }).width - sizes[key]) < 0.01;
        } else {
          const property = resolveProperty(action.intent, obj.type);
          if (property) active = propertyMatches(obj, property, action.value);
        }
      } else if (stagedTool) {
        // Stefan, 2026-09-03 — kein Objekt ausgewählt, aber ein Platzierungs-
        // werkzeug aktiv: zeigt, welche Eigenschaft für die NÄCHSTE Platzierung
        // vorgemerkt ist (`stagePrePlacementProperty()`). Nur der generische
        // Pfad — dieselbe Einschränkung wie beim Vormerken selbst.
        if (action.intent === 'line-path-strategy') {
          // No `resolveProperty` mapping exists for this intent (see
          // `stagePrePlacementProperty()`) — read the staged `pathStrategy`
          // directly, falling back to the registry's default ('straight', the
          // object contract's own unset-default, as `linePathStrategyOf`) so
          // the default choice shows as pre-highlighted too.
          const staged = (stagedProps?.['pathStrategy'] as string | undefined) ?? FALLBACK_STRATEGY_ID;
          active = staged === action.value;
        } else if (action.intent === 'checkbox-size') {
          // Same reasoning as `line-path-strategy` above — no `resolveProperty`
          // mapping (sets width+height together), so read the staged S/M/L
          // KEY directly rather than a resolved property name. Review-Lauf 02
          // (2026-09-03): fall back to 'M' (`createCheckboxDefaults`'s own
          // unset-default) so the default size shows as pre-highlighted too,
          // exactly like `line-path-strategy` already does above — this was
          // missing, so no S/M/L button ever lit up until something was
          // explicitly staged.
          const staged = (stagedProps?.['sizeKey'] as string | undefined) ?? 'M';
          active = staged === action.value;
        } else if (!O2dSvgOptionsbarElement.NOT_STAGEABLE_INTENTS.has(action.intent)) {
          const property = resolveProperty(action.intent, stagedTool as unknown as BoardObject['type']);
          if (property && stagedProps) {
            if (action.intent === 'text-size') {
              // Staged value is already the WORLD-UNIT fontSize (converted at
              // stage-time, see `stagePrePlacementProperty()`) — convert back
              // to a fine-step index for the same parent/exact-step matching
              // the post-creation path uses (`#fontSizeButtonActive`).
              const fontSize = stagedProps[property] as number | undefined;
              active = fontSize !== undefined
                && this.#fontSizeButtonActive(id, action.value as number, worldUnitsToFontIndex(fontSize));
            } else {
              active = stagedPropertyMatches(stagedProps, property, action.value);
            }
          }
        }
      }
      el.classList.toggle('is-active', active);
      el.classList.toggle('is-mixed', mixed);
    }
  };

  /** Whether a textbox is currently being EDITED (caret inside), as opposed to
   *  merely selected. Mirrors O2D.W-PRD-035 §R2's predicate: `liveListBlock` is
   *  defined (possibly `null`, for a caret in a plain paragraph) for the whole
   *  edit session, whereas `liveMarks` can be undefined mid-session — so keying
   *  on `liveMarks` alone would misread an edit session as a plain selection. */
  #isEditingText(): boolean {
    return (
      this.liveMarks !== undefined
      || this.liveListBlock !== undefined
      || this.liveAlign !== undefined
    );
  }

  /**
   * Anke, 2026-07-16 — resolve an align button against the caret's paragraph:
   * uniform → the matching button lights; blocks disagreeing (a selection across
   * differently aligned paragraphs) → nothing lights, the same rule she set for
   * alignment on a selected box. `off` (no alignment and no box default to
   * inherit) also lights nothing.
   */
  private resolveAlignActive(value: string | number): boolean {
    const mark = this.liveAlign;
    if (!mark || mark.state !== 'on' || mark.value === undefined) return false;
    return valueMatches(mark.value, value);
  }

  /**
   * Resolve a dimension of a SELECTED (not edited) textbox into the
   * `is-active` / `is-mixed` flags (Anke, 2026-07-16):
   *   uniform            → active on the matching button, whatever the dimension;
   *   several values      → dash every present value for size / colour, and show
   *                         NOTHING for every other dimension.
   *
   * Why only those two dash: a box mixing sizes or colours has no single honest
   * answer, and seeing WHICH sizes/colours it holds is useful. For the rest, a
   * dash would only say "it varies" — which the empty ring already says, without
   * the noise.
   */
  private resolveBoxState(
    dimension: DocumentDimension,
    value: string | number,
    snapshot: DocumentFormatSnapshot,
    id: string,
  ): { active: boolean; mixed: boolean } {
    const mark = snapshot[dimension];
    if (mark.state === 'mixed') {
      if (!BOX_DASH_DIMENSIONS.has(dimension)) return { active: false, mixed: false };
      return { active: false, mixed: this.isPresentOption(dimension, value, mark.present, id) };
    }
    if (mark.state !== 'on') return { active: false, mixed: false };
    if (dimension === 'fontSize') {
      return { active: this.fontSizeLevelMatches(mark.value, value, id), mixed: false };
    }
    if (mark.value === undefined) return { active: true, mixed: false }; // boolean dim ON
    return { active: valueMatches(mark.value, value), mixed: false };
  }

  /**
   * Resolve a per-run mark dimension against the live snapshot into the
   * mutually-exclusive `is-active` / `is-mixed` flags (O2D.W-PRD-027 §TR3/TR4):
   * MIXED → mixed; ON → active when the action's value matches the uniform
   * value (font-size via the cluster level conversion, DD2); OFF → neither.
   */
  private resolveMarkState(
    dimension: SelectionDimension,
    value: string | number,
    marks: SelectionMarkSnapshot,
    id: string,
  ): { active: boolean; mixed: boolean } {
    const mark = marks[dimension];
    if (mark.state === 'mixed') {
      // Boolean marks keep the whole-button dash (O2D.W-PRD-027 §TR3); valued
      // dimensions dash only the option(s) whose value is actually present in
      // the selection (O2D.W-PRD-031 §TR2), fail-quiet on an empty/absent set.
      if (BOOLEAN_DIMENSIONS.has(dimension)) return { active: false, mixed: true };
      return { active: false, mixed: this.isPresentOption(dimension, value, mark.present, id) };
    }
    if (mark.state !== 'on') return { active: false, mixed: false };
    if (dimension === 'fontSize') {
      return { active: this.fontSizeLevelMatches(mark.value, value, id), mixed: false };
    }
    if (mark.value === undefined) return { active: true, mixed: false }; // boolean mark ON
    return { active: valueMatches(mark.value, value), mixed: false };
  }

  /**
   * O2D.W-PRD-031 §TR2 — membership test for a MIXED valued dimension: an
   * option dashes only when its value is one of the distinct values actually
   * present in the selection. Fail-quiet — an empty/absent present-set matches
   * nothing (a conscious downgrade from the old blanket-dash). fontSize compares
   * via the px→level conversion, every other valued dim via the string compare.
   */
  private isPresentOption(
    dimension: DocumentDimension,
    value: string | number,
    present: readonly string[] | undefined,
    id: string,
  ): boolean {
    if (!present || present.length === 0) return false;
    if (dimension === 'fontSize') return present.some((px) => this.fontSizeLevelMatches(px, value, id));
    return present.some((v) => valueMatches(v, value));
  }

  /**
   * Whether a font-size button should highlight for the given current fine-step
   * index (1…24). A PARENT button (`action-fontsize-X`) lights up for its whole
   * group of three; a fine step (`action-fontsize-X-Y`) only on the exact size.
   * So clicking any size lights BOTH the parent group AND the exact fine step
   * (PRD requirement 3).
   */
  #fontSizeButtonActive(id: string, value: number, currentIndex: number): boolean {
    if (/^action-fontsize-\d+$/.test(id)) {
      return fontIndexGroup(currentIndex) === fontIndexGroup(value);
    }
    return value === currentIndex;
  }

  /**
   * Font-size highlight (O2D.W-PRD-027 §TR4 / DD2): convert the selection's
   * px mark to a fine-step index via `worldUnitsToFontIndex(px)` and match it
   * against the button (parent group vs. exact fine step). No cluster-factor
   * re-multiplication (Anke, 2026-08-13) — the stored mark is already the
   * real world-unit value (see zoom-cluster.service.ts); multiplying by the
   * CURRENT zoom's factor made the highlighted button drift after zooming,
   * even though the object/mark never changed.
   */
  private fontSizeLevelMatches(markValue: string | undefined, value: string | number, id: string): boolean {
    if (markValue === undefined) return false;
    const px = parseFloat(markValue);
    if (!Number.isFinite(px)) return false;
    return this.#fontSizeButtonActive(id, Number(value), worldUnitsToFontIndex(px));
  }

  /**
   * O2D.W-PRD-035 §R2 — resolve a list button's active state. WHILE EDITING
   * (a captured `liveListBlock`, including `null` for a plain paragraph) the
   * button is active iff the selection's block type equals its value —
   * selection-scoped. When NOT editing (`liveListBlock === undefined`) it falls
   * back to the advisory whole-textbox `obj.listType` (D2).
   */
  private resolveListActive(value: string | number, obj: BoardObject | undefined): boolean {
    if (this.liveListBlock !== undefined) {
      return this.liveListBlock === value;
    }
    if (!obj) return false;
    return propertyMatches(obj, 'listType', value);
  }

  /** The selected lines, when the selection is lines only (one or several);
   *  otherwise `[]`. Line commands act on all of them (Anke, 2026-10-03). */
  private selectedLines(): LineObject[] {
    return this.sameTypeSelection().filter(isLine);
  }

  private singleSelectedObject(): BoardObject | undefined {
    if (!this.board) return undefined;
    const ids = this.board.selectedIds;
    if (ids.length !== 1) return undefined;
    return this.board.boardState.getObject(ids[0]!);
  }

  /**
   * Anke, 2026-07-23 — die ausgewählten Objekte, wenn ALLE denselben Typ haben,
   * sonst `[]`. Grundlage dafür, bei einer gleichartigen Mehrfachauswahl den
   * typ-eigenen Optionskreis zu zeigen und eine Einstellung auf alle anzuwenden.
   *
   * Gruppen zählen nicht mit: In der Auswahl stehen ohnehin nur die zeichenbaren
   * Mitglieder (die Gruppen-Klammer hat keinen Knoten), aber falls doch eine
   * `group` auftaucht, würde sie die Typgleichheit brechen — und das ist richtig
   * so, denn eine Gruppe hat keinen eigenen Optionskreis.
   */
  private sameTypeSelection(): BoardObject[] {
    if (!this.board) return [];
    const objs = this.board.selectedIds
      .map((id) => this.board!.boardState.getObject(id))
      .filter((o): o is BoardObject => !!o);
    if (objs.length === 0) return [];
    const type = objs[0]!.type;
    return objs.every((o) => o.type === type) ? objs : [];
  }

  /**
   * Ein Stellvertreter-Objekt für Typ- und Property-Auflösung: das erste
   * ausgewählte Objekt, wenn die Auswahl EINHEITLICH ist (ein Objekt oder mehrere
   * gleichen Typs), sonst `undefined`. Angewandt wird die Einstellung über
   * `board.updateSelectedProperty`, das auf ALLE Ausgewählten wirkt.
   */
  private representativeObject(): BoardObject | undefined {
    return this.sameTypeSelection()[0];
  }

  /**
   * Anke, 2026-07-23 — den nativen Farbwähler öffnen und die Wahl zurückmelden.
   *
   * Ein kurzlebiges, unsichtbares `<input type="color">`: `click()` öffnet das
   * Farbwähler-Fenster des Browsers (Farbrad, Hex-Eingabe, zuletzt genutzte
   * Farben). `onInput` feuert bei jeder Änderung — dadurch folgt die Füllung dem
   * Wähler live; `onChange`/Schließen räumt das Element weg.
   *
   * `input type=color` braucht einen `#rrggbb`-Startwert. Werte, die keiner sind
   * (leer, 'transparent', Kurzform `#abc`), werden auf Schwarz normalisiert —
   * der Wähler braucht nur einen gültigen Anker, die eigentliche Wahl trifft der
   * Nutzer.
   */
  #openColorPicker(
    current: unknown,
    apply: (color: string) => void,
    anchor?: { left: number; top: number; width: number; height: number },
  ): void {
    const input = document.createElement('input');
    input.type = 'color';
    input.value = toColorInputValue(current);
    // Unsichtbar, aber TATSÄCHLICH gerendert und im Sichtfeld: `showPicker()`
    // (und Chromes `.click()`-Fallback) öffnen den Farbdialog NUR für ein
    // gerendertes Element. Ein weit ausgelagertes 0×0-Feld (`left:-9999px`,
    // `width:0`) galt als „nicht gerendert" und blieb stumm (Ankes Fehler).
    // Darum on-screen, 1×1, nur per `opacity:0` verborgen.
    //
    // Am angeklickten Farbfeld verankert (falls bekannt), damit der native
    // Wähler dort aufgeht statt oben links; ohne Anker als Rückfall bei (0,0).
    const left = anchor ? anchor.left + anchor.width / 2 : 0;
    const top = anchor ? anchor.top + anchor.height / 2 : 0;
    input.style.cssText =
      `position:fixed;left:${left}px;top:${top}px;width:1px;height:1px;opacity:0;border:0;padding:0;pointer-events:none`;
    document.body.appendChild(input);

    let done = false;
    const cleanup = (): void => {
      if (done) return;
      done = true;
      input.remove();
    };
    input.addEventListener('input', () => apply(input.value));
    // `change` feuert beim Bestätigen/Schließen; danach ist der Wähler fertig.
    input.addEventListener('change', () => { apply(input.value); cleanup(); });
    // Fallback, falls der Browser weder change noch blur sauber liefert.
    input.addEventListener('blur', cleanup);

    // `showPicker()` ist die dafür vorgesehene API (Chrome/Edge/Firefox/Safari)
    // und öffnet den Dialog zuverlässig innerhalb der Nutzer-Geste. Wo es fehlt
    // (ältere Browser, Test-Umgebung), der bewährte `.click()`-Fallback.
    const withPicker = input as HTMLInputElement & { showPicker?: () => void };
    try {
      if (typeof withPicker.showPicker === 'function') withPicker.showPicker();
      else input.click();
    } catch {
      input.click();
    }
  }
}

/** Einen beliebigen Property-Wert in einen für `input type=color` gültigen
 *  `#rrggbb`-String bringen; sonst Schwarz als Anker. */
function toColorInputValue(current: unknown): string {
  return typeof current === 'string' && /^#[0-9a-fA-F]{6}$/.test(current)
    ? current
    : '#000000';
}

declare global {
  interface HTMLElementTagNameMap {
    'o2d-svg-optionsbar': O2dSvgOptionsbarElement;
  }
}

// ---- Helpers -------------------------------------------------------------

/**
 * Compare a board object's property to the action's expected value.
 * Color comparisons are case-insensitive so `#22B573` and `#22b573` match.
 */
function propertyMatches(obj: BoardObject, property: string, value: string | number): boolean {
  const actual = (obj as unknown as Record<string, unknown>)[property];
  if (actual === undefined || actual === null) return false;
  if (typeof value === 'number') return actual === value;
  if (typeof value === 'string' && typeof actual === 'string') {
    return actual.toLowerCase() === value.toLowerCase();
  }
  return false;
}

/**
 * Stefan, 2026-09-03 — the pre-placement-staging counterpart to
 * `propertyMatches` above: same comparison rules, just reading a plain
 * `StagedToolProperties` bag instead of a live `BoardObject`'s property.
 */
function stagedPropertyMatches(props: StagedToolProperties, property: string, value: string | number): boolean {
  const actual = props[property];
  if (actual === undefined) return false;
  if (typeof value === 'number') return actual === value;
  if (typeof value === 'string' && typeof actual === 'string') {
    return actual.toLowerCase() === value.toLowerCase();
  }
  return false;
}

/**
 * Map an optionsbar action intent (+ value, for the two-way `text-decoration`)
 * to the per-run selection dimension it reflects (O2D.W-PRD-027 §TR3).
 * Returns `null` for object-level intents — they stay on the 2-state path and
 * are never `is-mixed` (FC2).
 */
function intentToDimension(intent: ActionIntent, value: string | number): SelectionDimension | null {
  switch (intent) {
    case 'font-style':          return 'italic';
    case 'text-decoration':
      return value === 'underline' ? 'underline'
           : value === 'line-through' ? 'strike'
           : null;
    case 'text-color':          return 'color';
    case 'text-size':           return 'fontSize';
    case 'text-weight':         return 'fontWeight';
    case 'text-transform':      return 'textTransform';
    case 'text-vertical-align': return 'verticalScript';
    default:                    return null;
  }
}

/**
 * Map an intent to the dimension the BOX-level (selected, not edited) snapshot
 * classifies. Extends the per-run mapping with the two block dimensions a caret
 * cannot express — alignment and list kind, which are per-paragraph.
 */
function intentToDocumentDimension(
  intent: ActionIntent,
  value: string | number,
): DocumentDimension | null {
  if (intent === 'text-align') return 'textAlign';
  if (intent === 'list-type') return 'listType';
  return intentToDimension(intent, value);
}

/**
 * The only dimensions that DASH when a selected box holds several values
 * (Anke, 2026-07-16) — every other dimension shows nothing at all when mixed.
 */
const BOX_DASH_DIMENSIONS = new Set<DocumentDimension>(['fontSize', 'color']);

/**
 * The three boolean per-run marks. A MIXED boolean dimension dashes its single
 * button as a whole (O2D.W-PRD-027 §TR3) — it carries no present-set — unlike a
 * valued dimension, which dashes only its present value slots (O2D.W-PRD-031 §TR2).
 */
const BOOLEAN_DIMENSIONS = new Set<SelectionDimension>(['italic', 'underline', 'strike']);

/**
 * Compare a valued dimension's uniform snapshot value to the action's expected
 * value. Case-insensitive (mirrors `propertyMatches`) so `#22B573`/`#22b573`
 * and `700`/`'700'` match across the string/number boundary.
 */
function valueMatches(markValue: string, actionValue: string | number): boolean {
  return markValue.toLowerCase() === String(actionValue).toLowerCase();
}

/** Minimal CSS.escape fallback (safe for our id format `action-…`). */
function cssEscape(id: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(id);
  return id.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

/**
 * Leitet aus Intent + Wert einen deutschen Tooltip-Text ab.
 * Wird in `bindActionHandlers` als SVG-`<title>`-Element eingefügt.
 */
function deriveTooltip(intent: string, value: string | number): string {
  const v = String(value).toUpperCase();
  switch (intent) {
    case 'fill':               return `Füllfarbe: ${v}`;
    case 'text-color':         return `Textfarbe: ${v}`;
    case 'border-color':       return `Rahmenfarbe: ${v}`;
    case 'border-width':       return `Rahmenstärke: ${value} px`;
    case 'border-style':
      return value === 'dashed' ? 'Gestrichelt'
           : value === 'dotted' ? 'Gepunktet'
           : 'Durchgezogen';
    case 'text-weight':
      return value === 300 ? 'Schrift dünn'
           : value === 700 ? 'Schrift fett'
           : 'Schrift normal';
    case 'text-align':
      return value === 'left'  ? 'Links ausrichten'
           : value === 'right' ? 'Rechts ausrichten'
           : 'Mittig ausrichten';
    case 'v-align':
      return value === 'top'    ? 'Text oben ausrichten'
           : value === 'bottom' ? 'Text unten ausrichten'
           : 'Text vertikal mittig';
    case 'list-type':
      return value === 'ordered'   ? 'Nummerierte Liste'
           : value === 'checklist' ? 'Checkliste'
           : 'Aufzählungsliste';
    case 'text-decoration':
      return value === 'underline' ? 'Unterstrichen' : 'Durchgestrichen';
    case 'font-style':         return 'Kursiv';
    case 'text-transform':
      return value === 'uppercase' ? 'Großbuchstaben' : 'Kleinbuchstaben';
    case 'text-vertical-align':
      return value === 'super' ? 'Hochgestellt' : 'Tiefgestellt';
    case 'box-shadow':         return 'Schatten ein/aus';
    case 'background-opacity': return 'Transparenz ein/aus';
    case 'border-radius':
      return value === 0  ? 'Eckig'
           : value === 8  ? 'Leicht abgerundet'
           : 'Stark abgerundet';
    case 'z-order':
      return value === 'front' ? 'Ganz nach vorne' : 'Ganz nach hinten';
    case 'board-command':
      return value === 'copy'      ? 'Kopieren'
           : value === 'duplicate' ? 'Duplizieren'
           : value === 'group'     ? 'Gruppieren'
           : value === 'degroup'   ? 'Gruppierung aufheben'
           : value === 'cut'    ? 'Ausschneiden'
           : value === 'paste'  ? 'Einfügen'
           : value === 'undo'   ? 'Rückgängig'
           : value === 'redo'   ? 'Wiederholen'
           : value === 'delete' ? 'Löschen'
           : '';
    case 'align':
      return value === 'left'    ? 'Linksbündig ausrichten (ab 2 Objekten)'
           : value === 'hcenter' ? 'Waagerecht mittig ausrichten (ab 2 Objekten)'
           : value === 'right'   ? 'Rechtsbündig ausrichten (ab 2 Objekten)'
           : value === 'top'     ? 'Oben ausrichten (ab 2 Objekten)'
           : value === 'vcenter' ? 'Senkrecht mittig ausrichten (ab 2 Objekten)'
           : value === 'bottom'  ? 'Unten ausrichten (ab 2 Objekten)'
           : '';
    case 'text-size':          return `Schriftgröße Stufe ${value}`;
    case 'line-cap-start':
    case 'line-cap-end': {
      const end   = intent === 'line-cap-start' ? 'Linienanfang' : 'Linienende';
      const shape = value === 'dot' ? 'Punkt' : value === 'arrow' ? 'Pfeil' : 'Quadrat';
      return `${end}: ${shape} (nochmal klicken = ohne)`;
    }
    case 'line-swap-caps':     return 'Enden tauschen';
    case 'line-path-strategy': return 'Linienform: gerade / rechtwinklig / Bezier';
    default:                   return '';
  }
}
